import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { useResetPassword } from "@/hooks/usePassword";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

type Phase = "form" | "done";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { toast } = useToast();
  const reset = useResetPassword();

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<Phase>("form");

  // No token in the URL → can't proceed. Show a clear dead-end.
  if (!token) {
    return (
      <AuthLayout
        eyebrow="Account recovery"
        title="Reset link invalid"
        sub="This password reset link is missing its token."
      >
        <Card className="border-brand-border shadow-brand-md">
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <CardDescription className="max-w-xs">
              Request a fresh link to continue.
            </CardDescription>
            <Button asChild className="mt-2">
              <Link to="/forgot-password">Request a new link</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (reset.isPending) return;

    if (password.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    try {
      const result = await reset.mutateAsync({ token, newPassword: password });
      switch (result) {
        case "SUCCESS":
          setPhase("done");
          break;
        case "TOKEN_EXPIRED":
          setError("This reset link has expired. Request a new one.");
          break;
        case "INVALID_TOKEN":
          setError("This reset link is invalid. Request a new one.");
          break;
        case "RESET_FAILED":
        default:
          setError(
            "Couldn't reset your password. Make sure it has at least 8 characters " +
            "with an uppercase letter, a number and a symbol."
          );
          break;
      }
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Something went wrong. Try again.";
      toast({ title: "Reset failed", description: message, variant: "destructive" });
    }
  }

  return (
    <AuthLayout
      eyebrow="Account recovery"
      title="Choose a new password"
      sub="Pick something strong you haven't used before."
    >
      <Card className="border-brand-border shadow-brand-md">
        {phase === "done" ? (
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <CardTitle className="text-xl">Password updated</CardTitle>
            <CardDescription className="max-w-xs">
              You can now sign in with your new password. For security, all other
              sessions have been signed out.
            </CardDescription>
            <Button className="mt-2" onClick={() => navigate("/login", { replace: true })}>
              Go to sign in
            </Button>
          </CardContent>
        ) : (
          <>
            <CardHeader className="pb-3">
              <CardTitle className="text-xl sm:text-2xl">New password</CardTitle>
              <CardDescription>This link expires 15 minutes after it was sent.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                  />
                </div>
                {error && (
                  <p className="text-sm font-medium text-destructive">{error}</p>
                )}
                <Button type="submit" disabled={reset.isPending} className="w-full" size="lg">
                  {reset.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Update password
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Need a new link?{" "}
        <Link to="/forgot-password" className="font-medium text-primary hover:underline">
          Request one
        </Link>
      </p>
    </AuthLayout>
  );
}
